import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";

import { toast } from "react-toastify";
import dayjs from "dayjs";
import ServicesTemplate from "../../templates/lookups/services";
import {
  createServiceRequest,
  deleteServiceRequest,
  getServiceDetailsRequest,
  getServicesRequest,
  updateServiceRequest,
} from "../../services/modules/lookups/services";
import { GetOccasionTypesRequest } from "../../services/modules/occasions/occasionsTypes";

function ServicesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.services.services);
  const count = useSelector((state) => state.services.count);
  const details = useSelector((state) => state.services.service);
  const load = useSelector((state) => state.services.load);
  const occasionTypes = useSelector(
    (state) => state.occasionsTypes.occasionTypes
  );

  //permissions - assuming similar permissions or public for now
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile
  );
  // You might need to add specific permissions for services if they exist in the backend/profile
  // For now using super_admin or assuming true for dev
  const isSuperAdmin = currentUser?.privilege == "super_admin";
  const can_create = isSuperAdmin; // Replace with actual permission check if available

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState({
    page_size: 10,
    page_number: 1,
    keyword: "",
    sort_by: "id",
  });

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getServiceDetailsRequest({ id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: filterObj,
    };
    dispatch(getServicesRequest(requestData));
  };

  const getOccasionTypes = async () => {
    dispatch(
      GetOccasionTypesRequest({
        params: {
          page_size: 1000,
          page_number: 1,
        },
      })
    );
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY HH:mm")}</>,
      image_url: data?.image_url ? (
        <img
          src={data?.image_url}
          alt={data?.name}
          width="125px"
          height="77px"
        />
      ) : (
        t("chat.no_image")
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/lookups/services",
      title: t("lookups_services.title"),
    },
    type && {
      active: false,
      title:
        type === "details" ? t("lookups_services.details") : id ? t("lookups_services.edit") : t("lookups_services.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    console.log("🚀 ~ handelSubmit ~ data:", data);
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/lookups/services");
      getList(filterObj);
      !id && formik?.resetForm();
      toast.success(
        id ? t("lookups_services.updated") : t("lookups_services.created")
      );
    };

    // Construct body based on API requirement
    const requestData = {
      body: {
        service: {
          image_url: data?.image,
          en: {
            name: data?.englishName,
          },
          ar: {
            name: data?.arabicName,
          },
          occasion_type_ids: data?.occasion_type_ids?.map((item) =>
            Number(item.value || item)
          ),
        },
      },
      action,
    };

    if (id) {
      //update call
      dispatch(updateServiceRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(createServiceRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    const action = () => {
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj((prev) => ({
          ...prev,
          page_number: page - 1,
        }));
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/lookups/services");
      toast.success(t("lookups_services.deleted"));
    };
    const requestData = {
      id,
      action,
    };
    dispatch(deleteServiceRequest(requestData));
    setOpenDeleteModal(false);
  };

  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin; // || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin; // || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  const headerChildren = <></>;
  const modalComponents = [];

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  useEffect(() => {
    getOccasionTypes();
  }, []);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <ServicesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={can_create}
        occasionTypes={occasionTypes}
      />
    </>
  );
}

export default ServicesPage;
